import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE_URL, getAuthHeaders, getJsonAuthHeaders } from "../../../utils/api";
import { getPermissions } from "../../../utils/permissions";
import { getPakistanPublicHolidays } from "../../../utils/pakistanHolidays";
import "./yearlyCalendar.css";

const EVENT_TYPES = ["Holiday", "Exam", "Event", "Deadline", "Other"];
const TYPE_COLORS = {
  Holiday: "#c41e24",
  Exam: "#8a6c1f",
  Event: "#2a4a7f",
  Deadline: "#c41e24",
  Other: "#5a5e6a",
  National: "#2e7d32",
  Public: "#eab308",
};
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const emptyForm = () => ({
  title: "",
  description: "",
  event_date: "",
  end_date: "",
  event_type: "Event",
  campus_id: "",
  color: "",
});

function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

function formatDisplayDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function YearlyCalendarPage({ variant = "app" }) {
  const role = (localStorage.getItem("als_role") || "").toLowerCase();
  const permissions = getPermissions(role);
  const canEdit = permissions.canEditCalendar;
  const [year, setYear] = useState(new Date().getFullYear());
  const [events, setEvents] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [printMenuOpen, setPrintMenuOpen] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/calendar/events?year=${year}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Unable to load calendar");
      }
      const data = await res.json();
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      setEvents([]);
      setError(err.message || "Unable to load calendar");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (!permissions.isSuperadmin) return;
    fetch(`${API_BASE_URL}/api/campuses`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setCampuses(Array.isArray(data) ? data : []))
      .catch(() => setCampuses([]));
  }, [permissions.isSuperadmin]);

  const publicHolidays = useMemo(() => getPakistanPublicHolidays(year), [year]);

  const eventsByDate = useMemo(() => {
    const map = {};
    const combined = [...publicHolidays, ...events].filter(
      (ev) => String(ev.event_type || "") !== "Meeting",
    );
    combined.forEach((ev) => {
      const start = ev.event_date;
      const end = ev.end_date || ev.event_date;
      let cursor = new Date(`${start}T00:00:00`);
      const last = new Date(`${end}T00:00:00`);
      while (cursor <= last) {
        const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(cursor.getDate()).padStart(2, "0")}`;
        if (!map[key]) map[key] = [];
        map[key].push(ev);
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    return map;
  }, [events, publicHolidays]);

  const yearAgenda = useMemo(() => {
    const combined = [...publicHolidays, ...events].filter(
      (ev) => String(ev.event_type || "") !== "Meeting",
    );
    const byId = new Map();
    combined.forEach((ev) => {
      if (!byId.has(String(ev.id))) byId.set(String(ev.id), ev);
    });
    return Array.from(byId.values()).sort((a, b) =>
      String(a.event_date).localeCompare(String(b.event_date)),
    );
  }, [publicHolidays, events]);

  const dayEvents = selectedDate ? eventsByDate[selectedDate] || [] : [];
  const dayHasPublicHoliday = dayEvents.some((ev) => ev.isSystemHoliday);
  const panelEvents = selectedDate ? dayEvents : yearAgenda;

  const logoUrl = `${process.env.PUBLIC_URL || ""}/als-logo.png`;
  const todayStr = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  }, []);

  const runPrint = (mode) => {
    setPrintMenuOpen(false);
    document.documentElement.classList.add("yc-print-active");
    document.body.classList.remove("yc-print-left", "yc-print-right", "yc-print-both");
    if (mode === "left") document.body.classList.add("yc-print-left");
    if (mode === "right") document.body.classList.add("yc-print-right");
    if (mode === "both") document.body.classList.add("yc-print-both");

    let pageStyle = document.getElementById("yc-print-page-style");
    if (!pageStyle) {
      pageStyle = document.createElement("style");
      pageStyle.id = "yc-print-page-style";
      document.head.appendChild(pageStyle);
    }
    pageStyle.textContent = `
      @media print {
        @page {
          size: A4 portrait;
          margin: 16mm 12mm 14mm;
        }
      }
    `;

    const cleanup = () => {
      document.body.classList.remove("yc-print-left", "yc-print-right", "yc-print-both");
      document.documentElement.classList.remove("yc-print-active");
      const el = document.getElementById("yc-print-page-style");
      if (el) el.remove();
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
        window.setTimeout(cleanup, 1000);
      });
    });
  };

  const openCreate = (dateStr) => {
    if (!canEdit) return;
    setEditing(null);
    setForm({ ...emptyForm(), event_date: dateStr || selectedDate || `${year}-01-01` });
    setFormOpen(true);
  };

  const openEdit = (ev) => {
    if (!canEdit || ev.isSystemHoliday) return;
    setEditing(ev);
    setForm({
      title: ev.title || "",
      description: ev.description || "",
      event_date: ev.event_date || "",
      end_date: ev.end_date || "",
      event_type: ev.event_type || "Event",
      campus_id: ev.campus_id != null ? String(ev.campus_id) : "",
      color: ev.color || "",
    });
    setFormOpen(true);
  };

  const saveEvent = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.event_date) return;
    if (editing?.isSystemHoliday) return;
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        event_date: form.event_date,
        end_date: form.end_date || null,
        event_type: form.event_type,
        campus_id: form.campus_id ? Number(form.campus_id) : null,
        color: form.color || TYPE_COLORS[form.event_type] || null,
      };
      const url = editing
        ? `${API_BASE_URL}/api/calendar/events/${editing.id}`
        : `${API_BASE_URL}/api/calendar/events`;
      const res = await fetch(url, {
        method: editing ? "PUT" : "POST",
        headers: getJsonAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Save failed");
      }
      setFormOpen(false);
      setEditing(null);
      await fetchEvents();
    } catch (err) {
      alert(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (ev) => {
    if (!canEdit || ev.isSystemHoliday) return;
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    const res = await fetch(`${API_BASE_URL}/api/calendar/events/${ev.id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.detail || "Delete failed");
      return;
    }
    await fetchEvents();
  };

  return (
    <div className={`yc-page yc-page--split ${variant === "teacher" ? "yc-page--teacher" : ""}`}>
      <div className="yc-print-header" aria-hidden="true">
        <img src={logoUrl} alt="" className="yc-print-logo" />
        <div className="yc-print-header__text">
          <strong>American Lycetuff</strong>
          <span>School Management System</span>
          <p>Yearly Calendar — Academic Year {year}</p>
        </div>
      </div>

      <div className="yc-header">
        <div>
          <h1>Yearly Calendar</h1>
          <p>
            {canEdit
              ? "Public holidays are marked. Customize school events on the right."
              : "Public & national holidays with school events for the year."}
          </p>
        </div>
        <div className="yc-header-actions">
          <div className="yc-year-stepper">
            <button type="button" onClick={() => setYear((y) => y - 1)} aria-label="Previous year">‹</button>
            <strong>{year}</strong>
            <button type="button" onClick={() => setYear((y) => y + 1)} aria-label="Next year">›</button>
          </div>
          <div className="yc-print-actions yc-no-print">
            <button type="button" className="yc-link-btn" onClick={() => setPrintMenuOpen(true)}>
              Print
            </button>
          </div>
          {canEdit && (
            <button
              type="button"
              className="yc-btn-primary yc-no-print"
              onClick={() => openCreate(selectedDate || `${year}-01-01`)}
            >
              + Add Event
            </button>
          )}
        </div>
      </div>

      <div className="yc-legend">
        <span className="yc-legend-item"><i style={{ background: "#2e7d32" }} /> National</span>
        <span className="yc-legend-item"><i style={{ background: "#eab308" }} /> Public</span>
        {EVENT_TYPES.filter((t) => t !== "Holiday").map((t) => (
          <span key={t} className="yc-legend-item">
            <i style={{ background: TYPE_COLORS[t] }} /> {t}
          </span>
        ))}
      </div>

      {error && <div className="yc-error">{error}</div>}

      <div className="yc-split">
        <section className="yc-split-left">
          <div className="yc-print-section-label" aria-hidden="true">Year Calendar</div>
          {loading ? (
            <div className="yc-loading">Loading calendar…</div>
          ) : (
            <div className="yc-grid yc-grid--compact">
              {MONTHS.map((monthName, monthIndex) => {
                const total = daysInMonth(year, monthIndex);
                const firstDow = new Date(year, monthIndex, 1).getDay();
                const cells = [];
                for (let i = 0; i < firstDow; i += 1) cells.push(null);
                for (let d = 1; d <= total; d += 1) cells.push(d);
                return (
                  <div className="yc-month" key={monthName}>
                    <div className="yc-month-title">{monthName}</div>
                    <div className="yc-weekdays">
                      {WEEKDAYS.map((w) => <span key={w}>{w}</span>)}
                    </div>
                    <div className="yc-days">
                      {cells.map((day, idx) => {
                        if (!day) return <span key={`e-${idx}`} className="yc-day yc-day--empty" />;
                        const dateStr = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                        const list = eventsByDate[dateStr] || [];
                        const active = selectedDate === dateStr;
                        const isHoliday = list.some((ev) => ev.isSystemHoliday);
                        const isToday = dateStr === todayStr;
                        return (
                          <button
                            type="button"
                            key={dateStr}
                            className={`yc-day ${list.length ? "yc-day--has" : ""} ${isHoliday ? "yc-day--holiday" : ""} ${isToday ? "yc-day--today" : ""} ${active ? "yc-day--active" : ""}`}
                            onClick={() => setSelectedDate(dateStr)}
                            title={list.map((e) => e.title).join(", ") || dateStr}
                          >
                            <span className="yc-day-num">{day}</span>
                            <span className="yc-dots">
                              {list.slice(0, 3).map((ev) => (
                                <i
                                  key={ev.id}
                                  style={{ background: ev.color || TYPE_COLORS[ev.event_type] || "#2a4a7f" }}
                                />
                              ))}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <aside className="yc-split-right yc-split-right--scroll">
          <div className="yc-print-section-label" aria-hidden="true">Event Agenda</div>
          <div className="yc-visual yc-visual--compact">
            <div className="yc-visual-overlay">
              <span className="yc-visual-kicker">Academic Year</span>
              <strong>{year}</strong>
              <p>
                {selectedDate
                  ? formatDisplayDate(selectedDate)
                  : `${yearAgenda.length} dated events · click a day for details`}
              </p>
            </div>
          </div>

          <div className="yc-agenda yc-agenda--flush">
            {selectedDate && (
              <div className="yc-agenda-toolbar">
                <span className="yc-muted">
                  {panelEvents.length} item{panelEvents.length === 1 ? "" : "s"} on this date
                </span>
                <button type="button" className="yc-link-btn" onClick={() => setSelectedDate(null)}>
                  Show all
                </button>
              </div>
            )}

            {selectedDate && dayHasPublicHoliday && (
              <p className="yc-holiday-banner">Public / national holiday marked on this day.</p>
            )}

            {panelEvents.length === 0 ? (
              <div className="yc-agenda-empty">
                <p>No events{selectedDate ? " on this day" : " yet"}.</p>
                {canEdit && (
                  <button type="button" className="yc-btn-primary" onClick={() => openCreate(selectedDate)}>
                    Add Event
                  </button>
                )}
              </div>
            ) : (
              <ul className="yc-event-list">
                {panelEvents.map((ev) => (
                  <li key={ev.id} className={ev.isSystemHoliday ? "yc-event--system" : ""}>
                    <div className="yc-event-date">{formatDisplayDate(ev.event_date)}</div>
                    <div className="yc-event-row">
                      <span className="yc-pill" style={{ background: ev.color || TYPE_COLORS[ev.event_type] }}>
                        {ev.isSystemHoliday ? (ev.holidayKind || "Public") : ev.event_type}
                      </span>
                      <strong>{ev.title}</strong>
                    </div>
                    {ev.description && <p>{ev.description}</p>}
                    <small>
                      {ev.isSystemHoliday
                        ? "System holiday (not editable)"
                        : (ev.campus_name || "All Campuses")}
                    </small>
                    {canEdit && !ev.isSystemHoliday && (
                      <div className="yc-event-actions">
                        <button type="button" onClick={() => openEdit(ev)}>Edit</button>
                        <button type="button" className="yc-danger" onClick={() => deleteEvent(ev)}>Delete</button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {canEdit && selectedDate && (
              <button type="button" className="yc-btn-primary yc-btn-block" onClick={() => openCreate(selectedDate)}>
                + Add event on this day
              </button>
            )}
          </div>
        </aside>
      </div>

      {printMenuOpen && (
        <div className="yc-modal-overlay yc-no-print" onClick={() => setPrintMenuOpen(false)}>
          <div className="yc-print-picker" onClick={(e) => e.stopPropagation()}>
            <h3>Print options</h3>
            <p className="yc-muted">Choose what to include in your printout.</p>
            <div className="yc-print-picker__actions">
              <button type="button" className="yc-link-btn" onClick={() => runPrint("left")}>
                Print calendar
              </button>
              <button type="button" className="yc-link-btn" onClick={() => runPrint("right")}>
                Print agenda
              </button>
              <button type="button" className="yc-link-btn yc-link-btn--primary" onClick={() => runPrint("both")}>
                Print both
              </button>
              <button type="button" className="yc-print-picker__cancel" onClick={() => setPrintMenuOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {formOpen && canEdit && (
        <div className="yc-modal-overlay" onClick={() => !saving && setFormOpen(false)}>
          <form className="yc-modal" onClick={(e) => e.stopPropagation()} onSubmit={saveEvent}>
            <h3>{editing ? "Edit Event" : "Add Event"}</h3>
            <label>
              Title
              <input
                required
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </label>
            <label>
              Type
              <select
                value={form.event_type}
                onChange={(e) => setForm((f) => ({ ...f, event_type: e.target.value }))}
              >
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <div className="yc-form-row">
              <label>
                Start date
                <input
                  type="date"
                  required
                  value={form.event_date}
                  onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))}
                />
              </label>
              <label>
                End date
                <input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                />
              </label>
            </div>
            {permissions.isSuperadmin && (
              <label>
                Campus
                <select
                  value={form.campus_id}
                  onChange={(e) => setForm((f) => ({ ...f, campus_id: e.target.value }))}
                >
                  <option value="">All Campuses</option>
                  {campuses.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Description
              <textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </label>
            <div className="yc-modal-actions">
              <button type="button" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</button>
              <button type="submit" className="yc-btn-primary" disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default YearlyCalendarPage;
